import * as React from "react";
import {
  flexRender,
  Row,
  RowData,
  useReactTable,
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  VisibilityState,
  RowSelectionState,
  ColumnFiltersState,
  SortingState,
  SortingFn,
  ColumnFilter,
  Cell,
} from "@tanstack/react-table";
import "./table.css";
import { Columns, Filter, MoreVertical } from "react-feather";
import { Button, Checkbox, Dropdown, Input, Modal } from "antd";
import { ReactSortable } from "react-sortablejs";
import clsx from "clsx";
import { arraysAreEqual } from "../../services/cpln";
import md5 from "md5";
import { ColumnSetting, ColumnSettings, TableCustomSortingFn, TableProps, TableSelectMode } from "./types";
import {
  TABLE_DEFAULT_COLUMN_FILTER,
  TABLE_DEFAULT_COLUMN_FILTER_TYPE,
  TABLE_DEFAULT_COLUMN_SIZE,
  TABLE_DEFAULT_FILTER,
  TABLE_DEFAULT_HIDE,
  TABLE_DEFAULT_RESIZE,
  TABLE_DEFAULT_SORT,
  cplnFilterFnFuzzy,
  cplnSortingFnDate,
  getTableStorageKeyColumnSettings,
  getTableStorageKeyColumnSizing,
  getTableStorageKeyGlobalFilter,
  getTableStorageKeySorting,
  valueIsAColumn,
} from "./utils";
import { ColumnResizeHandle } from "./components/ColumnResizeHandle";
import { Loader } from "../../components/layout/loader";
import { Tooltip } from "../../components/Tooltip";
import { ColumnSortIcons } from "./components/ColumnSortIcons";

export function Table<TData extends RowData>({
  tableId,
  title,
  selectMode = "none",
  selectKey,
  selections,
  initialSelections,
  onSelectionsChange,
  enableFilter = TABLE_DEFAULT_FILTER,
  filterPrefix,
  globalFilterFn = cplnFilterFnFuzzy,
  isLoading,
  hideSettings = false,
  data,
  columns: rawColumns,
  headerRenderer,
  noItemsFoundRenderer = () => <span className="text-lg">No items found</span>,
  styles = { header: {}, container: {} },
  classNames = { header: "", container: "" },
  maxHeight,
  defaultSorting,
  // Server-side mode props
  onSortChange,
  onSearchChange,
  // Infinite scroll props
  isLoadingMore,
  hasMore,
  onLoadMore,
}: TableProps<TData>) {
  /*
  topSection
  container
    table
  */

  const isServerSideMode = !!onSearchChange;

  const topSectionRef = React.useRef<HTMLDivElement>(null); // Hook 1
  const containerRef = React.useRef<HTMLDivElement>(null); // Hook 2
  const tableRef = React.useRef<HTMLTableElement>(null); // Hook 3

  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false); // Hook 4

  function getSortingFn(value?: SortingFn<TData> | TableCustomSortingFn) {
    if (!value) {
      return "text";
    }
    if (value === "cplnDate") {
      return (cplnSortingFnDate as unknown) as SortingFn<TData>;
    }
    return value as SortingFn<TData>;
  }

  function getColumns() {
    const columns: ColumnDef<TData>[] = [];
    for (const column of rawColumns) {
      columns.push({
        id: column.id,
        accessorKey: column.accessorFn ? undefined : column.id,
        accessorFn: column.accessorFn,
        cell: column.cell || ((p) => p.getValue()),
        size: column.size || TABLE_DEFAULT_COLUMN_SIZE,
        minSize: column.size,
        maxSize: column.maxSize,
        enableResizing: column.enableResize || TABLE_DEFAULT_RESIZE,
        enableHiding: column.enableHide || TABLE_DEFAULT_HIDE,
        enableColumnFilter: column.enableFilter || TABLE_DEFAULT_COLUMN_FILTER,
        meta: {
          filterVariant: column.columnFilterType || TABLE_DEFAULT_COLUMN_FILTER_TYPE,
        },
        enableSorting: column.enableSort || TABLE_DEFAULT_SORT,
        sortingFn: getSortingFn(column.sortingFn),
        sortDescFirst: false, // descending, not description

        header: column.label,
      });
    }
    return columns;
  }
  const columns = React.useMemo(getColumns, [rawColumns]); // Hook 5

  function getInitialColumnSetting(): ColumnSettings {
    const initialColumnSettings: any = [];
    for (const column of rawColumns) {
      let visible = true;
      if (column.hiddenByDefault === true) {
        visible = false;
      }
      initialColumnSettings.push({ id: column.id, visible: visible });
    }
    return initialColumnSettings;
  }

  function getDefaultColumnSettings(): ColumnSettings {
    const defaultColumnSettings = getInitialColumnSetting();
    const localValueString = localStorage.getItem(getTableStorageKeyColumnSettings(tableId)) || "";

    let parsedColumnSettings: ColumnSettings = [];
    try {
      parsedColumnSettings = JSON.parse(localValueString);
      if (parsedColumnSettings === null) {
        throw new Error("Saved column settings value cannot be null");
      }
      if (typeof parsedColumnSettings !== "object") {
        throw new Error("Saved column settings value must be of type object");
      }
      if (parsedColumnSettings.length < 1) {
        throw new Error("Saved column settings does not have any value");
      }

      // handles newly added columns
      for (const defaultColumnSetting of defaultColumnSettings) {
        if (!parsedColumnSettings.find((p) => p.id === defaultColumnSetting.id)) {
          parsedColumnSettings.push(defaultColumnSetting);
        }
      }

      // handles removed columns
      for (const parsedColumnSetting of parsedColumnSettings) {
        if (!defaultColumnSettings.find((d) => d.id === parsedColumnSetting.id)) {
          parsedColumnSettings = parsedColumnSettings.filter((p) => p.id !== parsedColumnSetting.id);
        }
      }

      return parsedColumnSettings;
    } catch (e) {
      console.log("getDefaultColumnSettings error", e.message);
    }
    return defaultColumnSettings;
  }

  const [columnSettings, setColumnSettings] = React.useState<ColumnSetting[]>(getDefaultColumnSettings()); // Hook 6
  const [temporaryColumnSettings, setTemporaryColumnSettings] = React.useState<ColumnSetting[]>( // Hook 7
    getDefaultColumnSettings(),
  );
  React.useEffect(() => {
    const encoded = JSON.stringify(columnSettings);
    localStorage.setItem(getTableStorageKeyColumnSettings(tableId), encoded);
    setTemporaryColumnSettings(columnSettings);
  }, [columnSettings]); // Hook 8

  function getColumnOrder() {
    let columnOrder: string[] = [];
    for (const columnSetting of columnSettings) {
      columnOrder.push(columnSetting.id);
    }
    return columnOrder;
  }
  const [columnOrder, setColumnOrder] = React.useState(getColumnOrder()); // Hook 9
  function getColumnVisibility() {
    let _columnVisibilityState: any = {};
    for (const columnSetting of columnSettings) {
      _columnVisibilityState[columnSetting.id] = columnSetting.visible;
    }
    return _columnVisibilityState;
  }
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(getColumnVisibility()); // Hook 10

  React.useEffect(() => {
    setColumnOrder(getColumnOrder());
    setColumnVisibility(getColumnVisibility());
  }, [columnSettings]); // Hook 11

  function toggleVisibility(id: string, confirm?: boolean) {
    const _columnSettings = JSON.parse(JSON.stringify(temporaryColumnSettings));
    const _columnSetting = _columnSettings.find((__columnSetting) => __columnSetting.id === id)!;
    _columnSetting.visible = !_columnSetting.visible;
    setTemporaryColumnSettings(_columnSettings);
    if (confirm) {
      setColumnSettings(_columnSettings);
    }
  }

  function getDefaultSortingState(): SortingState {
    const defaultSortingState: SortingState = defaultSorting || [{ id: "name", desc: false }];
    const localValueString = localStorage.getItem(`table-config-${tableId}-sorting`) || "";
    let parsedLocalValue: SortingState = undefined as any;
    try {
      parsedLocalValue = JSON.parse(localValueString);
      if (parsedLocalValue === null) {
        throw new Error("Saved sorting state value cannot be null");
      }
      if (typeof parsedLocalValue !== "object") {
        throw new Error("Saved sorting state value must be of type object");
      }
      if (!Array.isArray(parsedLocalValue)) {
        throw new Error("Saved sorting state value must be an array");
      }
      for (const item of parsedLocalValue) {
        if (!valueIsAColumn(item.id, rawColumns)) {
          throw new Error(`Column id ${item.id} is not within columns: ${rawColumns.map((c) => c.id)}`);
        }
      }
      return parsedLocalValue;
    } catch (e) {
      console.log(`Table ${tableId} failed to getDefaultSortingState | ${e.message}`);
      return defaultSortingState;
    }
  }
  const [sortingState, setSortingState] = React.useState<SortingState>(getDefaultSortingState()); // Hook 12
  React.useEffect(() => {
    const encoded = JSON.stringify(sortingState);
    localStorage.setItem(getTableStorageKeySorting(tableId), encoded);
  }, [sortingState]); // Hook 13

  // Handle sorting changes - intercept for server-side sort
  function handleSortingChange(updaterOrValue: SortingState | ((old: SortingState) => SortingState)) {
    const newState = typeof updaterOrValue === "function" ? updaterOrValue(sortingState) : updaterOrValue;
    setSortingState(newState);
    if (onSortChange) {
      if (newState.length === 0) {
        onSortChange({ by: "name", order: "asc" });
      } else {
        const sortItem = newState[0];
        const rawColumn = rawColumns.find((c) => c.id === sortItem.id);
        const by = rawColumn?.serverSortProperty || sortItem.id;
        const order = sortItem.desc ? "desc" : "asc";
        onSortChange({ by, order });
      }
    }
  }

  function toRowSelectionState(selectionArray: string[]) {
    let _selectionState: RowSelectionState = {};
    for (const selection of selectionArray) {
      _selectionState[selection] = true;
    }
    return _selectionState;
  }
  function getDefaultRowSelection(): RowSelectionState {
    const selectionsToUse = selections || initialSelections;
    if (!selectionsToUse) {
      return {};
    }
    return toRowSelectionState(selectionsToUse);
  }

  const [rowSelection, setRowSelection] = React.useState(getDefaultRowSelection()); // Hook 14
  const lastSelectedIndexRef = React.useRef<number | null>(null); // Hook 15

  function getRowSelectionArray(): { keys: string[]; items: TData[] } {
    const _keys: string[] = [];
    const _items: TData[] = [];
    for (const [key, selected] of Object.entries(rowSelection)) {
      if (!selected) {
        continue;
      }
      _keys.push(key);
      if (selectKey !== undefined) {
        const item = data.find((d) => d[selectKey] === key)!;
        _items.push(item);
      } else {
        const item = data[key];
        _items.push(item);
      }
    }
    return { keys: _keys, items: _items };
  }
  React.useEffect(() => {
    if (onSelectionsChange) {
      const { keys, items } = getRowSelectionArray();
      onSelectionsChange(keys, items);
    }
  }, [rowSelection]); // Hook 16
  React.useEffect(() => {
    if (!selections) {
      return;
    }
    const { keys } = getRowSelectionArray();
    if (arraysAreEqual(selections, keys)) {
      return;
    }
    setRowSelection(toRowSelectionState(selections));
  }, [selections]); // Hook 17

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]); // Hook 18

  function getDefaultGlobalFilter(): string {
    if (isServerSideMode) return "";
    const localValueString = localStorage.getItem(getTableStorageKeyGlobalFilter(tableId)) || "";
    return localValueString;
  }
  const [globalFilter, setGlobalFilter] = React.useState(getDefaultGlobalFilter()); // Hook 19
  React.useEffect(() => {
    if (!isServerSideMode) {
      localStorage.setItem(getTableStorageKeyGlobalFilter(tableId), globalFilter);
    }
  }, [globalFilter]); // Hook 20

  // Server-side search: local input state + debounced callback
  const [searchInputValue, setSearchInputValue] = React.useState(""); // Hook 21
  const searchTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null); // Hook 22
  React.useEffect(() => {
    if (!onSearchChange) return;
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      onSearchChange(searchInputValue);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInputValue]); // Hook 23

  function getDefaultColumnSizing(): { [_: string]: number } {
    try {
      const localValueString = localStorage.getItem(getTableStorageKeyColumnSizing(tableId)) || JSON.stringify({});
      const parsedLocalValue = JSON.parse(localValueString);
      for (const columnId of Object.keys(parsedLocalValue)) {
        if (!valueIsAColumn(columnId, rawColumns)) {
          throw new Error(
            `getDefaultColumnSizing has incorrect column id saved in storage | ${columnId} | Columns: ${rawColumns.map(
              (c) => c.id,
            )}`,
          );
        }
      }
      return parsedLocalValue;
    } catch (e) {
      return {};
    }
  }
  const [columnSizing, setColumnSizing] = React.useState<{ [_: string]: number }>(getDefaultColumnSizing()); // Hook 24
  React.useEffect(() => {
    localStorage.setItem(getTableStorageKeyColumnSizing(tableId), JSON.stringify(columnSizing));
  }, [columnSizing]); // Hook 25

  const table = useReactTable<TData>({
    // Hook 26
    data: data,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),

    // Filtering: use client-side filter when not in server-side mode
    ...(isServerSideMode
      ? {}
      : {
          getFilteredRowModel: getFilteredRowModel(),
          getFacetedRowModel: getFacetedRowModel(),
          getFacetedUniqueValues: getFacetedUniqueValues(),
        }),
    manualFiltering: isServerSideMode,
    enableGlobalFilter: enableFilter && !isServerSideMode,
    globalFilterFn: globalFilterFn,
    onGlobalFilterChange: setGlobalFilter,

    // Sorting: use client-side sort when no onSortChange callback
    ...(onSortChange ? {} : { getSortedRowModel: getSortedRowModel() }),
    manualSorting: !!onSortChange,
    onSortingChange: handleSortingChange,

    enableRowSelection: selectMode !== "none",
    enableMultiRowSelection: selectMode === "multi",
    getRowId: selectKey ? (row) => (row as any)[selectKey] : undefined, // default is index
    onRowSelectionChange: setRowSelection,

    onColumnSizingChange: setColumnSizing,
    columnResizeMode: "onChange",

    state: {
      columnOrder,
      columnVisibility,
      columnSizing,
      rowSelection,
      globalFilter: isServerSideMode ? undefined : globalFilter,
      sorting: sortingState,
    },

    meta: {
      columnFilters: columnFilters,
      setColumnFilters: setColumnFilters,
    },
  });

  // Get visible rows with column filter application
  const visibleRows: Row<TData>[] = React.useMemo(() => {
    // Hook 27
    const rows = table.getRowModel().rows;
    if (columnFilters.length === 0) return rows;
    return rows.filter((row) => {
      for (const columnConfig of rawColumns) {
        if (!columnConfig.enableFilter) continue;
        const columnFilter = columnFilters.find((cf) => cf.id === columnConfig.id);
        if (!columnFilter || !(columnFilter.value as string).length) continue;
        const rowValue = row.getValue(columnConfig.id) as string;
        switch (columnConfig.columnFilterType) {
          case "single":
            if ((columnFilter.value as string) !== rowValue) return false;
            break;
          case "multi":
            const values = (columnFilter.value as string).split(",").filter(Boolean);
            if (!values.includes(rowValue)) return false;
            break;
        }
      }
      return true;
    });
  }, [table.getRowModel().rows, columnFilters, rawColumns]);

  // Handler for row selection with shift+click support
  const handleRowSelect = React.useCallback(
    (rowIndex: number, rowId: string, event: React.MouseEvent) => {
      if (selectMode !== "multi") {
        // Single select mode - just set this one
        setRowSelection((prev) => {
          if (prev[rowId]) {
            return {};
          }
          return { [rowId]: true };
        });
        lastSelectedIndexRef.current = rowIndex;
        return;
      }

      if (event.shiftKey && lastSelectedIndexRef.current !== null) {
        // Shift+click: select range from anchor to current, preserving existing selections
        const start = Math.min(lastSelectedIndexRef.current, rowIndex);
        const end = Math.max(lastSelectedIndexRef.current, rowIndex);

        // Use functional form to get latest state and preserve existing selections
        setRowSelection((prev) => {
          const newSelection = { ...prev };
          for (let i = start; i <= end; i++) {
            const row = visibleRows[i];
            if (row) {
              newSelection[row.id] = true;
            }
          }
          return newSelection;
        });
      } else {
        // Normal click: toggle and set anchor
        // Delete key when deselecting to avoid indeterminate state issues
        setRowSelection((prev) => {
          const newSelection = { ...prev };
          const isDeselecting = !!newSelection[rowId];
          if (isDeselecting) {
            delete newSelection[rowId];
            // Clear anchor on deselect so next shift+click won't range select
            lastSelectedIndexRef.current = null;
          } else {
            newSelection[rowId] = true;
            // Only set anchor when selecting
            lastSelectedIndexRef.current = rowIndex;
          }
          return newSelection;
        });
      }
    },
    [selectMode, visibleRows],
  ); // Hook 28

  const [containerParentWidth, setContainerParentWidth] = React.useState(0); // Hook 29
  React.useEffect(() => {
    const callback = () => {
      setContainerParentWidth(containerRef.current?.parentElement?.clientWidth || 0);
    };
    callback();
    window.addEventListener("resize", callback);
    return () => {
      window.removeEventListener("resize", callback);
    };
  }, []); // Hook 30

  // to fix jumping on inital open while it calculates the width
  const [initialFramesPassed, setInitialFramesPassed] = React.useState<boolean | null>(null); // Hook 31
  React.useEffect(() => {
    if (initialFramesPassed === null) {
      setInitialFramesPassed(false);
    } else if (!initialFramesPassed) {
      setInitialFramesPassed(true);
    }
  }, [initialFramesPassed]); // Hook 32

  const filterRef = React.useRef(null as any); // Hook 33
  React.useEffect(() => {
    if (!enableFilter) {
      return;
    }
    setTimeout(() => {
      filterRef.current?.focus();
    }, 0);
  }, []);
  // Hook 34

  // Infinite scroll sentinel
  const sentinelRef = React.useRef<HTMLDivElement>(null); // Hook 35
  React.useEffect(() => {
    if (!onLoadMore || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );
    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, isLoadingMore]); // Hook 36

  function getTableWidth(): number | "100%" {
    const tableTotalSize = table.getTotalSize();

    if (isLoading || table.getCoreRowModel().rows.length < 1 || table.getRowModel().rows.length < 1) {
      return "100%";
    }

    // Only set explicit width if columns exceed container, otherwise let CSS handle it
    if (tableTotalSize > containerParentWidth && containerParentWidth > 0) {
      let tableWidth = tableTotalSize;
      if (maxHeight) {
        tableWidth -= 20;
      }
      return tableWidth;
    }

    return "100%";
  }
  const tableWidth = getTableWidth();

  return (
    <>
      <div
        ref={topSectionRef}
        className={`flex items-center justify-between mb-4 gap-2 ${classNames.header}`}
        style={styles.header}
      >
        {title ? <>{typeof title === "string" ? <div className="text-2xl">{title}</div> : title("text-2xl")}</> : null}
        <div className="flex-grow" />
        {enableFilter ? (
          <>
            {filterPrefix}
            {isServerSideMode ? (
              <Input
                ref={filterRef}
                placeholder="Search"
                style={{ width: 240 }}
                value={searchInputValue}
                onChange={(e) => setSearchInputValue(e.target.value)}
                allowClear
              />
            ) : (
              <Input
                ref={filterRef}
                placeholder="Search"
                style={{ width: 240 }}
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                allowClear
              />
            )}
          </>
        ) : null}
        {hideSettings ? null : (
          <>
            <Tooltip title={"Column Settings"}>
              <Button type="default" onClick={() => setIsSettingsOpen(true)} icon={<Columns />} />
            </Tooltip>
          </>
        )}
        {headerRenderer ? headerRenderer(table) : null}
      </div>
      <div
        ref={containerRef}
        className={clsx(`table-container`, classNames.container)}
        style={{ ...styles.container, maxHeight: styles.container?.maxHeight || maxHeight }}
      >
        <table ref={tableRef} style={{ width: tableWidth }}>
          <thead>
            {/* Headers */}
            <tr className="relative">
              {/* Show headers if it is groups or has any item */}
              {initialFramesPassed &&
                table.getLeafHeaders().map((header, headerIndex) => {
                  const rawColumn = rawColumns.find((rawColumn) => rawColumn.id === header.column.id)!;
                  if (!rawColumn) {
                    console.error(
                      `Need to fix that column config is not found for the column with id: ${header.column.id}`,
                    );
                    console.info(rawColumns);
                    console.info("header");
                    console.info(header);
                    console.error(`-----`);
                  }

                  const totalHeaderCount = table.getLeafHeaders().length;
                  const isLastHeader = totalHeaderCount - 1 === headerIndex;

                  let sortTooltipTitle = "";
                  switch (header.column.getIsSorted()) {
                    case "asc":
                      sortTooltipTitle = "Sorted by Ascending Order";
                      break;
                    case "desc":
                      sortTooltipTitle = "Sorted by Descending Order";
                      break;
                    case false:
                      sortTooltipTitle = "Not Sorted";
                      break;
                  }

                  return (
                    <th
                      className="relative"
                      key={header.id}
                      style={{
                        width: header.getSize(),
                      }}
                      colSpan={header.colSpan}
                    >
                      <div className="flex items-center">
                        {headerIndex === 0 && selectMode !== "none" ? (
                          <div style={{ width: 30, minWidth: 30 }} className="flex">
                            {selectMode === "single" ? (
                              <span className="invisible">X</span>
                            ) : (
                              <Checkbox
                                aria-label={`table-${tableId}-row-selection-all`}
                                checked={table.getIsAllRowsSelected()}
                                indeterminate={table.getIsSomeRowsSelected()}
                                onChange={() => table.toggleAllRowsSelected()}
                              />
                            )}
                          </div>
                        ) : null}
                        {header.column.getCanSort() ? (
                          <button
                            type={"button"}
                            className={`flex items-center cursor-pointer ngfocus`}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                            <Tooltip title={sortTooltipTitle}>
                              {header.column.getIsSorted() === "desc" ? (
                                <ColumnSortIcons active={"down"} />
                              ) : header.column.getIsSorted() === "asc" ? (
                                <ColumnSortIcons active={"up"} />
                              ) : (
                                <ColumnSortIcons active={"none"} />
                              )}
                            </Tooltip>
                          </button>
                        ) : (
                          <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                        )}
                        <div className="flex-grow" />
                        {rawColumn.enableFilter && !isServerSideMode ? (
                          <Dropdown
                            trigger={["click"]}
                            popupRender={() => {
                              const uniqueValues = Array.from(header.column.getFacetedUniqueValues().keys());

                              return (
                                <div
                                  className="p-2 shadow-sm border"
                                  style={{ backgroundColor: "var(--color-drop)" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                >
                                  {uniqueValues.map((value) => {
                                    // @ts-expect-error
                                    const columnFilters = table.options.meta!.columnFilters;
                                    // @ts-expect-error
                                    const setColumnFilters = table.options.meta!.setColumnFilters;
                                    const columnFilter = columnFilters.find((x) => x.id === header.column.id);
                                    const currentSelectedValues = ((columnFilter?.value as string) || "").split(",");
                                    const restOfTheColumnFilters = columnFilters.filter(
                                      (x) => x.id !== header.column.id,
                                    );
                                    let newColumnFilters: ColumnFilter[] = [];
                                    return (
                                      <div
                                        className="p-1"
                                        key={`${"label"}---${value}`}
                                        onClick={(e) => {
                                          e.stopPropagation();

                                          switch (rawColumn.columnFilterType) {
                                            case "single":
                                              let currentValue = (columnFilter?.value as string) || "";
                                              newColumnFilters = [
                                                ...restOfTheColumnFilters,
                                                { id: header.column.id, value: currentValue === value ? "" : value },
                                              ];
                                              setColumnFilters(newColumnFilters);
                                              break;
                                            case "multi":
                                              let currentValues = ((columnFilter?.value as string) || "").split(",");
                                              if (currentValues.includes(value)) {
                                                currentValues = currentValues.filter((c) => c !== value);
                                              } else {
                                                currentValues.push(value);
                                              }
                                              newColumnFilters = [
                                                ...restOfTheColumnFilters,
                                                { id: header.column.id, value: currentValues.join(",") },
                                              ];
                                              setColumnFilters(newColumnFilters);
                                              break;
                                          }
                                        }}
                                      >
                                        <Checkbox
                                          aria-label={value}
                                          checked={currentSelectedValues.includes(value)}
                                          // TODO fix
                                          onChange={(e) => {}}
                                        >
                                          {value}
                                        </Checkbox>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            }}
                          >
                            <button
                              type={"button"}
                              className={`cursor-pointer ngfocus color-link-hover`}
                              onClick={() => {}}
                            >
                              <Filter
                                className={`h-4 ${
                                  ((columnFilters.find((x) => x.id === header.column.id)?.value as string) || "")
                                    .length > 0
                                    ? "fill-current color-link"
                                    : ""
                                }`}
                              />
                            </button>
                          </Dropdown>
                        ) : null}
                        <div
                          className={`header-separator ${isLastHeader ? "header-separator-last" : ""} ${
                            header.column.getCanResize() ? "resize-handler" : ""
                          }`}
                          onDoubleClick={header.column.getCanResize() ? () => header.column.resetSize() : undefined}
                          onMouseDown={header.column.getCanResize() ? header.getResizeHandler() : undefined}
                          onTouchStart={header.column.getCanResize() ? header.getResizeHandler() : undefined}
                        >
                          {header.column.getCanResize() ? <ColumnResizeHandle /> : null}
                        </div>
                      </div>
                    </th>
                  );
                })}
            </tr>
          </thead>
          {/* CoreRowModel is without any filtering */}
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={table.getLeafHeaders().length}>
                  <span className="infobox flex items-center justify-center">
                    <Loader
                      style={{
                        width: 100,
                        height: 100,
                        margin: 0,
                        left: "unset",
                        right: "unset",
                        top: "unset",
                        bottom: "unset",
                      }}
                    />
                  </span>
                </td>
              </tr>
            ) : table.getCoreRowModel().rows.length < 1 ? (
              <tr>
                <td colSpan={table.getLeafHeaders().length}>
                  <span className="infobox flex items-center justify-center">{noItemsFoundRenderer(table)}</span>
                </td>
              </tr>
            ) : visibleRows.length < 1 ? (
              <tr>
                <td colSpan={table.getLeafHeaders().length}>
                  <span className="infobox flex flex-col items-center justify-center">
                    <span className="text-lg mb-2">No filtered items found</span>
                    <Button
                      type="primary"
                      onClick={() => {
                        setGlobalFilter("");
                        setSearchInputValue("");
                        setColumnFilters([]);
                        if (onSearchChange) {
                          onSearchChange("");
                        }
                      }}
                      size={"small"}
                    >
                      Clear Filter here
                    </Button>
                  </span>
                </td>
              </tr>
            ) : (
              visibleRows.map((row, rowIndex) => {
                const selected = getRowSelectionArray().keys.includes(row.id);
                const visibleCells = row.getVisibleCells();
                return (
                  <RowRenderer
                    key={row.id}
                    selected={selected}
                    selectMode={selectMode}
                    row={row as any}
                    rowIndex={rowIndex}
                    visibleCells={visibleCells as any}
                    columnOrder={JSON.stringify(columnOrder)}
                    sort={JSON.stringify(sortingState)}
                    onSelect={handleRowSelect}
                  />
                );
              })
            )}
            {isLoadingMore ? (
              <tr>
                <td colSpan={table.getLeafHeaders().length}>
                  <span className="flex items-center justify-center py-8">
                    <Loader
                      style={{
                        width: 40,
                        height: 40,
                        margin: 0,
                        left: "unset",
                        right: "unset",
                        top: "unset",
                        bottom: "unset",
                      }}
                    />
                  </span>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
        {hasMore && !isLoadingMore ? <div ref={sentinelRef} style={{ height: 1 }} /> : null}
      </div>
      {isSettingsOpen ? (
        <Modal
          open={isSettingsOpen}
          title={"Table Settings"}
          maskClosable={false}
          onOk={() => {
            setColumnSettings(temporaryColumnSettings);
            setIsSettingsOpen(false);
          }}
          onCancel={() => {
            setTemporaryColumnSettings(columnSettings);
            setIsSettingsOpen(false);
          }}
        >
          <div className="pl-5">
            <ReactSortable
              handle=".drag-handle"
              className="flex flex-col gap-2"
              list={JSON.parse(JSON.stringify(temporaryColumnSettings))}
              setList={(newState, sortable, state) => {
                setTemporaryColumnSettings(newState as any[]);
              }}
            >
              {temporaryColumnSettings.map((columnSetting) => {
                const column = rawColumns.find((_column) => _column.id === columnSetting.id)!;
                const tableColumn = table.getAllColumns().find((c) => c.id === column.id)!;
                if (!column.label) {
                  return <span />;
                }

                return (
                  <div key={columnSetting.id} className={clsx("flex items-center relative")}>
                    <TableDragHandle />
                    <Checkbox
                      aria-label={`column-visibility-setting-${columnSetting.id}`}
                      checked={columnSetting.visible}
                      disabled={!tableColumn.getCanHide()}
                      onChange={() => {
                        toggleVisibility(columnSetting.id);
                      }}
                    />
                    <div className={`basis-0 flex-grow ml-1`}>{column.label}</div>
                  </div>
                );
              })}
            </ReactSortable>
          </div>
          <div className="mt-4">
            <Button
              type="primary"
              size="small"
              onClick={() => {
                setTemporaryColumnSettings(getInitialColumnSetting());
              }}
            >
              Reset to Defaults
            </Button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

interface RowRendererProps<TData> {
  row: Row<TData>;
  rowIndex: number;
  selectMode: TableSelectMode;
  selected: boolean;
  columnOrder: string;
  visibleCells: Cell<TData, unknown>[];
  sort: string;
  onSelect?: (rowIndex: number, rowId: string, event: React.MouseEvent) => void;
}
const RowRenderer = React.memo(
  function RowRenderer<TData extends RowData>({
    row,
    rowIndex,
    selectMode,
    selected,
    visibleCells,
    onSelect,
  }: RowRendererProps<TData>) {
    return (
      <tr className="item-row">
        {visibleCells.map((cell, cellIndex) => (
          <td
            key={cell.id}
            style={{
              width: cell.column.getSize(),
            }}
          >
            <div className="flex items-center">
              {cellIndex === 0 && selectMode !== "none" ? (
                <div style={{ width: 30, minWidth: 30 }}>
                  <Checkbox
                    aria-label={`row-selection-row-${row.id}`}
                    checked={selected}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onSelect) {
                        onSelect(rowIndex, row.id, e);
                      } else {
                        row.toggleSelected();
                      }
                    }}
                    onChange={() => {}} // Required by Ant Design but we use onClick for shift detection
                  />
                </div>
              ) : null}
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </div>
          </td>
        ))}
      </tr>
    );
  },
  (prevProps, nextProps) => {
    const prevObj = prevProps.row.original;
    const prevHash = md5(JSON.stringify(prevObj));
    const prevSelected = prevProps.selected;
    const prevSort = prevProps.sort;
    const prevColumnOrder = prevProps.columnOrder;
    const prevRowIndex = prevProps.rowIndex;
    const prevCells = prevProps.visibleCells
      .map((c) => c.id)
      .sort()
      .join();

    const nextObj = nextProps.row.original;
    const nextHash = md5(JSON.stringify(nextObj));
    const nextSelected = nextProps.selected;
    const nextSort = nextProps.sort;
    const nextColumnOrder = nextProps.columnOrder;
    const nextRowIndex = nextProps.rowIndex;
    const nextCells = nextProps.visibleCells
      .map((c) => c.id)
      .sort()
      .join();

    return (
      prevHash === nextHash &&
      prevSelected === nextSelected &&
      prevCells === nextCells &&
      prevSort === nextSort &&
      prevColumnOrder === nextColumnOrder &&
      prevRowIndex === nextRowIndex &&
      prevProps.onSelect === nextProps.onSelect
    );
  },
);

export function TableDragHandle() {
  return (
    <div
      className="absolute cursor-move drag-handle"
      style={{
        width: 16,
        height: 30,
        left: 0,
        borderRadius: 3,
        transform: "translateX(-20px)",
        color: "gray",
      }}
    >
      <MoreVertical className="absolute left-0" style={{ left: 2, top: 3, width: 18 }} />
      <MoreVertical className="absolute" style={{ left: -4, top: 3, width: 18 }} />
    </div>
  );
}
